import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { IAudition } from '../../types/global/helper';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useDispatch } from 'react-redux';
import { navigate } from '../../redux/navigator/action';
import { ROUTES } from '../../types/global/routes.types';
import { replaceRouteParam } from '../../utils/routesProcessor';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { MessageActions } from './MessageActions';
import type { IMessage } from '../../redux/messages/types';
interface AuditionMessageChatProps {
    audition: IAudition;
    isCurrentUser: boolean;
    message?: IMessage;
    onReactionClick?: (emoji: string) => void;
    onDeleteMessage?: (messageId: string) => void;
    formatMessageTime?: (timestamp?: string) => string;
}

export const AuditionMessageChat: React.FC<AuditionMessageChatProps> = ({
    audition,
    isCurrentUser,
    message,
    onReactionClick = () => {},
    onDeleteMessage,
    formatMessageTime = () => '',
}) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();

    const isExpired = audition.timeToExpiration < 1;

    const handleDetailsClick = () => {
        const route = replaceRouteParam(ROUTES.AUDITION_DETAIL, 'auditionId', audition.slug);
        dispatch(
            navigate({
                routes: `${route}`,
                skipAuthentication: true,
            }),
        );
    };

    return (
        <Box
            sx={{
                borderRadius: '16px',
                overflow: 'hidden',
                width: { xs: '100%', sm: '280px' },
                backgroundColor: theme[themeStyle].deep_grey,
                border: `1px solid ${theme[themeStyle].grey}30`,
            }}>
            {/* Cover Image */}
            <Box sx={{ width: '100%', height: '150px', position: 'relative', overflow: 'hidden' }}>
                <CustomLazyLoadImage
                    src={audition.imageCover}
                    alt={audition.title}
                    effect="blur"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />
            </Box>

            {/* Content */}
            <Box sx={{ p: 2, flex: 1 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: '16px',
                        color: theme[themeStyle].tag_white,
                        mb: 1,
                    }}>
                    {audition.title}
                </Typography>

                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: '12px',
                        color: theme[themeStyle].grey,
                    }}>
                    {message?.message_content || audition.plot || 'No message content'}
                </Typography>

                <Button
                    variant="contained"
                    disabled={isExpired}
                    onClick={handleDetailsClick}
                    fullWidth
                    sx={{
                        backgroundColor: isExpired ? theme[themeStyle].grey : theme[themeStyle].highlight,
                        color: theme[themeStyle].tag_white,
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: isExpired ? theme[themeStyle].grey : theme[themeStyle].highlight,
                        },
                        '&.Mui-disabled': {
                            color: theme[themeStyle].tag_white,
                            opacity: 0.4,
                        },
                    }}>
                    {isExpired ? 'Expired' : 'Details'}
                </Button>

                {/* Message actions - only if message prop is provided */}
                {message && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: isCurrentUser ? 'rgba(255, 255, 255, 0.7)' : theme[themeStyle].grey,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                            }}>
                            {formatMessageTime(message.createdAt)}
                        </Typography>

                        {/* Add message actions */}
                        <MessageActions
                            message={message}
                            isCurrentUser={isCurrentUser}
                            onReactionClick={onReactionClick}
                            onDeleteMessage={onDeleteMessage}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};
